import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { addToWishlist, removeFromWishlist } from "../../../components/Wishlist/WishlistSlice";
import { useCartWishlist } from "../../../app/CartWishlistContext";
import { useNavigate } from "react-router-dom";
import { getBestseller } from "../services/bestseller.service";

export const useBestsellerSlider = ({ onQuickView }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getCartWishlistCount } = useCartWishlist();

  const sliderRef = useRef(null);
  const [bestsellers, setBestsellers] = useState([]);
  const [animatedWish, setAnimatedWish] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const [transitionEnabled, setTransitionEnabled] = useState(true);

  const cardWidth = 220;
  const gap = 20;

  /* ---------------- FETCH API DATA ---------------- */
  useEffect(() => {
    const fetchBestsellers = async () => {
      const products = await getBestseller();

      if (products.length > 1) {
        const first = products[1];
        const rest = products.filter((_, i) => i !== 1);
        setBestsellers([first, ...rest]);
      } else {
        setBestsellers(products);
      }

      setIsLoggedIn(!!localStorage.getItem("token"));
    };

    fetchBestsellers();
  }, []);

  /* ---------------- MOBILE CHECK ---------------- */
  useEffect(() => {
    const resize = () => setIsMobile(window.innerWidth <= 768);
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  /* ---------------- WISHLIST ---------------- */
  const toggleWishlist = async (e, product) => {
    e.stopPropagation();

    if (!isLoggedIn) {
      navigate("/login");
      return;
    }

    try {
      if (product.is_wishlisted) {
        const wishlistId = product?.wishlist?.[0]?.wishlist_id;
        if (!wishlistId) return;

        await dispatch(removeFromWishlist(wishlistId)).unwrap();
        getCartWishlistCount();

        setBestsellers((prev) =>
          prev.map((p) =>
            p.id === product.id
              ? { ...p, is_wishlisted: false, wishlist: [] }
              : p
          )
        );

        // toast.success("Removed from wishlist");
      } else {
        const res = await dispatch(
          addToWishlist({ product_id: product.id })
        ).unwrap();

        getCartWishlistCount();
        setAnimatedWish(product.id);

        setBestsellers((prev) =>
          prev.map((p) =>
            p.id === product.id
              ? {
                ...p,
                is_wishlisted: true,
                wishlist: [{ wishlist_id: res?.id }],
              }
              : p
          )
        );

        setTimeout(() => setAnimatedWish(null), 1500);
        // toast.success("Added to wishlist");
      }
    } catch {
      toast.error("Something went wrong");
    }
  };

  /* ---------------- SLIDER LOGIC ---------------- */
  const slidingProducts = bestsellers.slice(1);
  const totalProducts = [...slidingProducts, ...slidingProducts];

  const handlePrev = () => {
    setTransitionEnabled(true);
    setSlideIndex((p) => p - 1);
  };

  const handleNext = () => {
    setTransitionEnabled(true);
    setSlideIndex((p) => p + 1);
  };

  useEffect(() => {
    const len = slidingProducts.length;

    if (slideIndex < 0) {
      setTimeout(() => {
        setTransitionEnabled(false);
        setSlideIndex(len - 1);
      }, 600);
    } else if (slideIndex >= len) {
      setTimeout(() => {
        setTransitionEnabled(false);
        setSlideIndex(0);
      }, 600);
    }
  }, [slideIndex, slidingProducts.length]);

  return {
    bestsellers,
    animatedWish,
    isMobile,
    showLoginPrompt,
    setShowLoginPrompt,
    toggleWishlist,
    sliderRef,
    slideIndex,
    transitionEnabled,
    totalProducts,
    cardWidth,
    gap,
    handlePrev,
    handleNext,
  };
};